import { useState, useEffect, useCallback, useMemo } from 'react';
import AppleLogo from 'Shared/icons/logos/apple.svg';
import { loadAppleSdk } from 'Shared/appleHelper';
import { isAndroidMobileApp } from 'Shared/utils/mobile-app-helpers';
import { useTranslations } from '@ifixit/i18n/hooks';
import { RecentlyUsedBadge } from './RecentlyUsedBadge';
import { Auth } from 'Shared/auth';

export function AppleSSOButton({
   loginHandler,
   setError,
   text,
   recentlyUsed,
}: {
   loginHandler: (response: any) => void;
   setError: (error: string) => void;
   text: string;
   recentlyUsed?: boolean;
}) {
   const t = useTranslations('Login');
   const [isLoading, setIsLoading] = useState(false);
   const [isReady, setIsReady] = useState(false);
   const onAndroidMobileApp = useMemo(() => isAndroidMobileApp(), []);

   useEffect(() => {
      if (!App.appleLoginEnabled || !App.appleClientId) {
         return;
      }

      loadAppleSdk(() => setIsReady(true));
   }, []);

   const handleClick = useCallback(async () => {
      if (!isReady || isLoading) return;

      setIsLoading(true);
      try {
         const response = await window.AppleID.auth.signIn();

         // Send the identity token to our backend
         const apiResponse = await fetch('/api/2.0/user/login/appleLogin', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               identityToken: response.authorization.id_token,
               user: response.user,
            }),
         });

         const result = await apiResponse.json();

         if (result.loggedIn) {
            result.from = 'apple';
            (Auth as any).loggedIn(result);
            loginHandler(result);
         } else {
            setError(result.reason || t('unableToCompleteAppleLogin'));
         }
      } catch (error: any) {
         // User cancelled or other error
         if (error?.error !== 'popup_closed_by_user') {
            setError(t('unableToCompleteAppleLogin'));
         }
      } finally {
         setIsLoading(false);
      }
   }, [isReady, isLoading, loginHandler, setError, t]);

   if (!App.appleLoginEnabled || onAndroidMobileApp) {
      return null;
   }

   const disabled = !isReady || isLoading;

   return (
      <div
         id="appleLogin"
         data-mobile-appid="appleLogin"
         onClick={handleClick}
         aria-disabled={disabled}
         className={disabled ? 'pointer-events-none' : 'cursor-pointer'}
      >
         <button type="button" className="button-sso-provider z-1 w-full" disabled={disabled}>
            <AppleLogo className="size-5 overflow-visible" />
            <span className="min-w-0 truncate">{text}</span>
         </button>
         {recentlyUsed && <RecentlyUsedBadge />}
      </div>
   );
}
